import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { RecordActions } from '../../components/RecordActions';
import { RECORD_FIELDS } from '../../components/recordFields';
import { Pill } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { dateLabel, money, quotationStatus } from '../../lib/format';
import { QuotationEditor } from './QuotationEditor';
import { QuotationDetail } from './QuotationDetail';
import type { Quotation } from '../../lib/types';

export function QuotationsPage() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const canWrite = can(user, 'quotations', 'create');
  const [reloadSignal, setReloadSignal] = useState(0);
  // mode: 关闭 / 新建 / 编辑（携带待编辑报价单）
  const [editor, setEditor] = useState<{ mode: 'new' } | { mode: 'edit'; q: Quotation } | null>(null);

  const refresh = () => setReloadSignal((n) => n + 1);

  const columns: Column<Quotation>[] = [
    { header: '报价单号', render: (q) => (
      <span className="num strong">{q.number}</span>
    ) },
    { header: '客户', render: (q) => <span>{q.customer_name || '—'}</span> },
    { header: '报价日期', render: (q) => <span className="num">{dateLabel(q.quotation_date)}</span> },
    { header: '有效期至', render: (q) => <span className="num muted">{dateLabel(q.valid_until)}</span> },
    { header: '含税总额', align: 'right', render: (q) => <span className="num strong">{money(q.grand_total, q.currency)}</span> },
    { header: '状态', render: (q) => { const s = quotationStatus(q.status); return <Pill tone={s.tone} label={s.label} />; } },
    { header: '版本', align: 'right', render: (q) => <span className="muted num">{q.version && q.version > 1 ? `v${q.version}` : '—'}</span> },
  ];

  return (
    <>
      {editor ? (
        <QuotationEditor
          initial={editor.mode === 'edit' ? editor.q : null}
          onCancel={() => setEditor(null)}
          onSaved={(saved) => {
            setEditor(null);
            refresh();
            // 让被保存的单号进入列表搜索，方便定位结果。
            const url = new URL(window.location.href);
            url.searchParams.set('q', saved.number);
            window.history.replaceState({}, '', url.toString());
          }}
        />
      ) : null}

      <ListPage<Quotation>
        title="报价单"
        desc="报价单 · /api/quotations，支持新建/编辑草稿、复制、版本修订、状态流转、转合同、西语打印与审计留痕（当页展开，不用抽屉）"
        endpoint="/api/quotations"
        columns={columns}
        rowId={(q) => q.id}
        searchPlaceholder="搜索报价单号 / 客户 / 状态 / 备注…"
        initialQuery={sp.get('q') || ''}
        reloadSignal={reloadSignal}
        rowActions={(row) => <RecordActions type="quotations" row={row} fields={RECORD_FIELDS['quotations']} label={String(row.number)} onDone={() => setReloadSignal((n) => n + 1)} />}
        actions={canWrite ? (
          <button className="btn primary" onClick={() => setEditor((e) => (e && e.mode === 'new' ? null : { mode: 'new' }))}>
            {editor?.mode === 'new' ? '收起新建' : '新建报价单'}
          </button>
        ) : undefined}
        renderDetail={(row) => (
          <QuotationDetail
            row={row}
            onEdit={(q) => setEditor({ mode: 'edit', q })}
            onChanged={refresh}
          />
        )}
      />
      {!canWrite ? (
        <div className="notice info" style={{ marginTop: 'var(--gap)' }}>
          当前角色为只读。报价单的新建、编辑与状态流转由销售与管理员执行（服务端强制校验）。
        </div>
      ) : null}
    </>
  );
}
